import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  BookOpen, BarChart3, Grid3X3, Users, GraduationCap,
  TrendingUp, AlertTriangle, RefreshCw, Download, Filter,
  Table, LayoutGrid, Loader2, ArrowLeft, Calendar, Bell, FileText, Calculator, Bug
} from 'lucide-react';
import { PageHeader } from '@/components/ui-dna';

// Componentes - Blocos 1-7
import VisaoGlobal from './VisaoGlobal';
import TabelaNotas from './TabelaNotas';
import MatrizNotas from './MatrizNotas';
import MapaTurma from './MapaTurma';
import MapaDisciplina from './MapaDisciplina';
import HeatmapNotas from './HeatmapNotas';

// Componentes - Blocos 8-16
import { PainelTrimestre } from './PainelTrimestre';
import { DetalheAluno } from './DetalheAluno';
import { RelatorioRecuperacao } from './RelatorioRecuperacao';
import { AlertasInteligentes } from './AlertasInteligentes';
import { MotorMedias } from './MotorMedias';

const API_URL = window.location.origin;

export default function CentralNotas() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('visao-global');
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Filtros selecionados
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedTrimestre, setSelectedTrimestre] = useState('');
  const [selectedTurma, setSelectedTurma] = useState('');
  const [selectedDisciplina, setSelectedDisciplina] = useState('');

  // Diagnóstico (admin only) — lê role do localStorage
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  let isAdmin = false;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    isAdmin = ['admin', 'coordenacao', 'diretor', 'gestor'].includes(String(u.role || '').toLowerCase());
  } catch {
    isAdmin = false;
  }

  const handleOpenDiagnostico = async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagData(null);
    try {
      const params = { year: selectedYear };
      if (selectedTrimestre) params.trimestre = selectedTrimestre;
      if (selectedTurma) params.turma_id = selectedTurma;
      const r = await api.get('/central-notas/visao-global', { params });
      setDiagData(r?.data?.debug || { _empty: true });
    } catch (e) {
      toast.error('Falha ao carregar diagnóstico: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setDiagLoading(false);
    }
  };
  
  useEffect(() => {
    loadFiltros();
  }, []);
  
  const loadFiltros = async () => {
    try {
      const res = await api.get('/central-notas/filtros');
      setFiltros(res.data);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
      toast.error('Erro ao carregar filtros');
    } finally {
      setLoading(false);
    }
  };
  
  const clearFilters = () => {
    setSelectedTrimestre('');
    setSelectedTurma('');
    setSelectedDisciplina('');
  };
  
  const filtrosAtivos = [selectedTrimestre, selectedTurma, selectedDisciplina].filter(Boolean).length;
  
  // Função de exportação
  const handleExport = async (formato = 'csv', tipo = 'resumo') => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ 
        formato, 
        tipo, 
        year: selectedYear 
      });
      if (selectedTrimestre) params.append('trimestre', selectedTrimestre);
      if (selectedTurma) params.append('class_group_id', selectedTurma);
      
      const res = await fetch(`${API_URL}/api/central-notas/exportar?${params}`);
      const data = await res.json();
      
      if (formato === 'csv' && data.csv) {
        // Download CSV
        const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `central_notas_${tipo}_${selectedYear}.csv`;
        link.click();
        toast.success(`Exportação concluída: ${data.total} registros`);
      } else {
        toast.success(`Dados carregados: ${data.total} registros`);
      }
    } catch (error) {
      console.error('Erro na exportação:', error);
      toast.error('Erro ao exportar dados');
    } finally {
      setExporting(false);
    }
  };
  
  // Função de sincronização DNACore
  const handleSync = async () => {
    setSyncing(true);
    try {
      const trimestre = selectedTrimestre || '1º Trimestre';
      const res = await api.post(`/dnacore/sincronizar?year=${selectedYear}&trimestre=${encodeURIComponent(trimestre)}`);
      
      if (res.data.success) {
        toast.success(res.data.mensagem || 'Sincronização concluída!');
        // Recarregar filtros para atualizar dados
        loadFiltros();
      } else {
        toast.error('Erro na sincronização: ' + (res.data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro na sincronização:', error);
      toast.error('Erro ao sincronizar dados');
    } finally {
      setSyncing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-green-500 animate-pulse mx-auto" />
            <BookOpen className="w-10 h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-600 mt-4 text-lg">Carregando Central de Notas...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50" data-testid="central-notas">
      {/* Header DNA Design System (ui-dna) */}
      <div className="sticky top-0 z-40 backdrop-blur-xl shadow-sm">
        <PageHeader
          icon={BookOpen}
          eyebrow="Acadêmico · Premium"
          title="Central de Notas"
          subtitle="Visualização acadêmica global"
          onClose={() => navigate('/dnacore-v2')}
          rightSlot={
            <div className="flex items-center gap-2 flex-wrap" data-testid="central-notas-filters">
              {/* Ano */}
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[92px] h-8 bg-white/15 border-white/25 text-white text-xs hover:bg-white/25" data-testid="filter-year">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {(filtros?.anos || [new Date().getFullYear()]).map((ano) => (
                    <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Trimestre */}
              <Select value={selectedTrimestre || "all"} onValueChange={(v) => setSelectedTrimestre(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[130px] h-8 bg-white/15 border-white/25 text-white text-xs hover:bg-white/25" data-testid="filter-trimestre">
                  <SelectValue placeholder="Trimestre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(filtros?.trimestres || []).map((tri) => (
                    <SelectItem key={tri} value={tri}>{tri}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Turma */}
              <Select value={selectedTurma || "all"} onValueChange={(v) => setSelectedTurma(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] h-8 bg-white/15 border-white/25 text-white text-xs hover:bg-white/25" data-testid="filter-turma">
                  <SelectValue placeholder="Turmas" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Todas as Turmas</SelectItem>
                  {(filtros?.turmas || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Disciplina */}
              <Select value={selectedDisciplina || "all"} onValueChange={(v) => setSelectedDisciplina(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] h-8 bg-white/15 border-white/25 text-white text-xs hover:bg-white/25" data-testid="filter-disciplina">
                  <SelectValue placeholder="Disciplinas" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Todas Disciplinas</SelectItem>
                  {(filtros?.disciplinas || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filtrosAtivos > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="h-8 bg-white/15 border-white/25 text-white hover:bg-white/25"
                  data-testid="clear-filters"
                >
                  Limpar ({filtrosAtivos})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-white/15 border-white/25 text-white hover:bg-white/25"
                onClick={() => handleExport('csv', 'resumo')}
                disabled={exporting}
                data-testid="export-button"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Exportar
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 bg-white/15 border-white/25 text-white hover:bg-white/25"
                  onClick={handleOpenDiagnostico}
                  data-testid="btn-diagnostico"
                  title="Auditoria de integridade dos dados (admin)"
                >
                  <Bug className="w-4 h-4 mr-1" />
                  Diagnóstico
                </Button>
              )}
              <Button
                size="sm"
                className="h-8 bg-white text-violet-700 hover:bg-emerald-50 hover:text-emerald-700 font-semibold"
                onClick={handleSync}
                disabled={syncing}
                data-testid="sync-button"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Atualizar
              </Button>
            </div>
          }
        />
      </div>
      
      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full max-w-6xl mx-auto mb-8 bg-white/80 border border-purple-200/50 p-1.5 rounded-2xl shadow-sm flex-wrap h-auto gap-1">
            <TabsTrigger 
              value="visao-global"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-visao-global"
            >
              <BarChart3 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Visão</span> Global
            </TabsTrigger>
            <TabsTrigger 
              value="tabela"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-tabela"
            >
              <Table className="w-4 h-4 mr-1" />
              Tabela
            </TabsTrigger>
            <TabsTrigger 
              value="matriz"
              className="flex-1 min-w-[70px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-matriz"
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Matriz
            </TabsTrigger>
            <TabsTrigger 
              value="turma"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-turma"
            >
              <Users className="w-4 h-4 mr-1" />
              Turma
            </TabsTrigger>
            <TabsTrigger 
              value="disciplina"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-600 data-[state=active]:to-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-disciplina"
            >
              <GraduationCap className="w-4 h-4 mr-1" />
              Disciplina
            </TabsTrigger>
            <TabsTrigger 
              value="heatmap"
              className="flex-1 min-w-[70px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-heatmap"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Heatmap
            </TabsTrigger>
            <TabsTrigger 
              value="trimestre"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-violet-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-trimestre"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Trimestre
            </TabsTrigger>
            <TabsTrigger 
              value="recuperacao"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-recuperacao"
            >
              <FileText className="w-4 h-4 mr-1" />
              Recuperação
            </TabsTrigger>
            <TabsTrigger 
              value="alertas"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-alertas"
            >
              <Bell className="w-4 h-4 mr-1" />
              Alertas
            </TabsTrigger>
            <TabsTrigger 
              value="motor"
              className="flex-1 min-w-[110px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-motor"
            >
              <Calculator className="w-4 h-4 mr-1" />
              Motor de Médias
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="visao-global">
            <VisaoGlobal 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              turmaId={selectedTurma}
            />
          </TabsContent>
          
          <TabsContent value="tabela">
            <TabelaNotas 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              turmaId={selectedTurma}
              disciplinaId={selectedDisciplina}
              filtros={filtros}
            />
          </TabsContent>
          
          <TabsContent value="matriz">
            <MatrizNotas 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              turmaId={selectedTurma}
            />
          </TabsContent>
          
          <TabsContent value="turma">
            <MapaTurma 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              turmas={filtros?.turmas || []}
              initialTurmaId={selectedTurma}
            />
          </TabsContent>
          
          <TabsContent value="disciplina">
            <MapaDisciplina 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              disciplinas={filtros?.disciplinas || []}
              initialDisciplinaId={selectedDisciplina}
            />
          </TabsContent>
          
          <TabsContent value="heatmap">
            <HeatmapNotas 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="trimestre">
            <PainelTrimestre 
              year={parseInt(selectedYear)}
              filtros={filtros}
            />
          </TabsContent>
          
          <TabsContent value="recuperacao">
            <RelatorioRecuperacao 
              year={parseInt(selectedYear)}
              filtros={filtros}
              onSelectAluno={(id) => setSelectedStudentId(id)}
            />
          </TabsContent>
          
          <TabsContent value="alertas">
            <AlertasInteligentes 
              year={parseInt(selectedYear)}
              filtros={filtros}
              onSelectAluno={(id) => setSelectedStudentId(id)}
            />
          </TabsContent>
          
          <TabsContent value="motor">
            <MotorMedias
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre || '1º Trimestre'}
              classGroupId={selectedTurma}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Modal Detalhe do Aluno */}
      <DetalheAluno 
        studentId={selectedStudentId}
        year={parseInt(selectedYear)}
        onClose={() => setSelectedStudentId(null)}
      />

      {/* Modal Diagnóstico (admin) */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="diagnostico-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <Bug className="w-5 h-5" /> Diagnóstico — Central de Notas
            </DialogTitle>
            <DialogDescription className="text-xs">
              Auditoria de integridade dos lançamentos oficiais. Filtros aplicados: ano <b>{selectedYear}</b>
              {selectedTrimestre && <> · trimestre <b>{selectedTrimestre}</b></>}
              {selectedTurma && <> · turma <b>{selectedTurma}</b></>}.
            </DialogDescription>
          </DialogHeader>
          <div className="p-2">
            {diagLoading && (
              <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
            )}
            {!diagLoading && diagData && !diagData._empty && (
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="diagnostico-grid">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[10px] text-slate-500 uppercase">Total bruto (registros)</p>
                  <p className="text-xl font-bold text-slate-800">{diagData.total_grade_records ?? 0}</p>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <p className="text-[10px] text-emerald-700 uppercase">Notas válidas</p>
                  <p className="text-xl font-bold text-emerald-700">{diagData.valid_grade_records ?? 0}</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-[10px] text-amber-700 uppercase">Órfãos ignorados</p>
                  <p className="text-xl font-bold text-amber-700">{diagData.ignored_orphans ?? 0}</p>
                  <p className="text-[10px] text-amber-600 mt-1">Sem subject_id/subject_name</p>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
                  <p className="text-[10px] text-rose-700 uppercase">Descartadas</p>
                  <p className="text-xl font-bold text-rose-700">
                    {Math.max((diagData.total_grade_records ?? 0) - (diagData.valid_grade_records ?? 0), 0)}
                  </p>
                  <p className="text-[10px] text-rose-600 mt-1">Valor "-"/null/NaN ou tipo fora dos 4 oficiais</p>
                </div>
                <div className="col-span-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
                  <p className="text-[10px] text-purple-700 uppercase">Tipos oficiais (somam média)</p>
                  <p className="text-sm font-semibold text-purple-700">{(diagData.tipos_oficiais || []).join(' · ')}</p>
                </div>
                <p className="col-span-2 text-[10px] text-slate-400 mt-1">Calculado em: {diagData.calculated_at}</p>
              </div>
            )}
            {!diagLoading && diagData?._empty && (
              <p className="text-sm text-slate-500">Nenhum dado de diagnóstico disponível.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
